import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { SaveProfileRequest, SaveProfileResponse } from "@myProfile/models";
import { openNotificationThunk } from "@store/Notification/thunks";

/**
 * Saves profile changes
 * API: POST /entitlements/v1/user/UserProfile/updateProfile
 *
 * Response when profile updates without email change:
 * { success: true, message: "Profile updated successfully.", guid: "...", emailChangeRequested: false }
 *
 * Response when profile updates with email change:
 * { success: true, message: "Profile updated successfully. Email change request submitted for approval.", guid: "...", emailChangeRequested: true }
 */
export const saveProfileThunk = createAsyncThunk<SaveProfileResponse, SaveProfileRequest, ThunkApiConfig>(
  "myProfile/saveProfile",
  async (profileData, { extra, dispatch, rejectWithValue }) => {
    try {
      const url = "/entitlements/v1/user/UserProfile/updateProfile";
      const response = await extra<SaveProfileResponse, SaveProfileRequest>(url, {
        method: "POST",
        body: profileData,
      });

      if (!response || !response.data) {
        return rejectWithValue("Failed to save profile");
      }

      const successText = response.data.emailChangeRequested
        ? "Profile updated. Email change request submitted for approval."
        : "Profile updated.";

      // Show success notification
      dispatch(
        openNotificationThunk({
          variant: "info",
          text: successText,
        })
      );

      return response.data;
    } catch (error: unknown) {
      let errorMessage = "Failed to save profile. Please try again.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };

        errorMessage = axiosError.response?.data?.message || errorMessage;
      }

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);
